import { QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import i18n from 'App/i18n';

// ============================================================================
// API Error Types
// ============================================================================

export interface ValidationError {
  name: string;
  description: string;
}

export interface ApiErrorResponse {
  validationErrors?: ValidationError[];
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Extract a user-friendly error message from an API error response.
 * Handles validation errors, general API errors, and network errors.
 */
export const getApiErrorMessage = (error: unknown): string => {
  // Handle Axios errors with response data
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as ApiErrorResponse;

    // If there are validation errors, join them into a message
    if (data.validationErrors && data.validationErrors.length > 0) {
      return data.validationErrors.map(e => e.description).join('\n');
    }

    // Use detail or title from the error response
    if (data.detail) {
      return data.detail;
    }

    if (data.title) {
      return data.title;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback
  return 'An unexpected error occurred';
};

/**
 * Show an error toast with the extracted API error message.
 * The fallbackKey is a translation key that will be used if no error message is extracted.
 */
export const showApiError = (error: unknown, fallbackKey: string = 'save_failed'): void => {
  const message = getApiErrorMessage(error);
  // If we got an actual error message from the API, show it directly
  // Otherwise, translate the fallback key
  toast.error(message || i18n.t(fallbackKey));
};

/**
 * Show validation errors as individual toasts (useful for multiple field errors).
 */
export const showValidationErrors = (error: unknown): boolean => {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as ApiErrorResponse;

    if (data.validationErrors && data.validationErrors.length > 0) {
      data.validationErrors.forEach(e => {
        toast.error(e.description);
      });
      return true;
    }
  }
  return false;
};

// ============================================================================
// Query Client Configuration
// ============================================================================

/**
 * TanStack Query client configuration
 *
 * Default settings:
 * - staleTime: 5 minutes - data considered fresh, won't refetch
 * - gcTime: 30 minutes - unused data kept in cache before garbage collection
 * - refetchOnWindowFocus: true - refetch when user returns to tab
 * - retry: 1 - retry failed requests once
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,    // 5 minutes
      gcTime: 30 * 60 * 1000,      // 30 minutes
      refetchOnWindowFocus: true,
      retry: 1,
    },
    mutations: {
      onError: (error: unknown) => {
        // Default error handler for mutations - show API error message
        showApiError(error);
      },
    },
  },
});

/**
 * Query key factory for consistent key generation
 *
 * Usage:
 *   queryKeys.products.all          -> ['products']
 *   queryKeys.products.detail(1)    -> ['products', 1]
 *   queryKeys.products.list({...})  -> ['products', 'list', {...}]
 */
export const queryKeys = {
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.products.details(), id] as const,
  },
  tanks: {
    all: ['tanks'] as const,
    lists: () => [...queryKeys.tanks.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.tanks.lists(), filters] as const,
    details: () => [...queryKeys.tanks.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.tanks.details(), id] as const,
    balances: () => [...queryKeys.tanks.all, 'balances'] as const,
    surfaceLevels: () => [...queryKeys.tanks.all, 'surfaceLevels'] as const,
  },
  terminals: {
    all: ['terminals'] as const,
    lists: () => [...queryKeys.terminals.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.terminals.lists(), filters] as const,
    details: () => [...queryKeys.terminals.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.terminals.details(), id] as const,
  },
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.customers.lists(), filters] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.customers.details(), id] as const,
  },
  inventory: {
    all: ['inventory'] as const,
    lists: () => [...queryKeys.inventory.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.inventory.lists(), filters] as const,
    details: () => [...queryKeys.inventory.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.inventory.details(), id] as const,
  },
  measurements: {
    all: ['measurements'] as const,
    lists: () => [...queryKeys.measurements.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.measurements.lists(), filters] as const,
    details: () => [...queryKeys.measurements.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.measurements.details(), id] as const,
  },
  flowMeters: {
    all: ['flowMeters'] as const,
    lists: () => [...queryKeys.flowMeters.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.flowMeters.lists(), filters] as const,
    unattached: (timestamp?: string) => [...queryKeys.flowMeters.all, 'unattached', timestamp] as const,
  },
  productMixtures: {
    all: ['productMixtures'] as const,
    lists: () => [...queryKeys.productMixtures.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.productMixtures.lists(), filters] as const,
  },
  transportationCompanies: {
    all: ['transportationCompanies'] as const,
    lists: () => [...queryKeys.transportationCompanies.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.transportationCompanies.lists(), filters] as const,
    drivers: () => [...queryKeys.transportationCompanies.all, 'drivers'] as const,
    vehicles: () => [...queryKeys.transportationCompanies.all, 'vehicles'] as const,
  },
  deliveryDestinations: {
    all: ['deliveryDestinations'] as const,
    lists: () => [...queryKeys.deliveryDestinations.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.deliveryDestinations.lists(), filters] as const,
    details: () => [...queryKeys.deliveryDestinations.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.deliveryDestinations.details(), id] as const,
  },
  loadingInfos: {
    all: ['loadingInfos'] as const,
    lists: () => [...queryKeys.loadingInfos.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.loadingInfos.lists(), filters] as const,
  },
  approval: {
    all: ['approval'] as const,
    unapproved: () => [...queryKeys.approval.all, 'unapproved'] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
  },
  reporting: {
    all: ['reporting'] as const,
    billingReport: (filters?: Record<string, unknown>) => [...queryKeys.reporting.all, 'billing', filters] as const,
    entriesReport: (filters?: Record<string, unknown>) => [...queryKeys.reporting.all, 'entries', filters] as const,
    customersReport: (filters?: Record<string, unknown>) => [...queryKeys.reporting.all, 'customers', filters] as const,
    tanksReport: (filters?: Record<string, unknown>) => [...queryKeys.reporting.all, 'tanks', filters] as const,
  },
  billing: {
    all: ['billing'] as const,
    billedIds: () => [...queryKeys.billing.all, 'billedIds'] as const,
  },
};
